import { React, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { HomeDetails as HomeDetailsRedesign } from '@components/HomeDetails/HomeDetails';
import { HomeDetails as PreviousHomeDetails } from '@components/FindAHomeDetails/HomeDetails';
import { getModelDetails } from '@services/homesService';
import {
  getWebpageTemplateContent,
  getDynamicImageGrid,
} from '@services/contentfulService';
import { getDealerLandingPageInfoById } from '@services/dealerService';
import { testModels } from '@components/HomeDetails/testModels';
import { LocationsContext } from '@contexts/LocationsContext';
import * as locationService from '@services/locationService';

const HomeDetails = ({
  homeDetails,
  pageInfo,
  imageGridData,
  dealerInfo,
  geocode,
}) => {
  const [homeDetailsObject, setHomeDetailsObject] = useState(homeDetails);
  const [dealerInfoObject, setDealerInfoObject] = useState(dealerInfo);
  const locationsContext = useContext(LocationsContext);

  useEffect(() => {
    // Check for valid locationsContext.geocode before using or this will generate a client-side exception.
    // The locations context may still be loading the geocode if navigating directly to a home detail page and on incognito mode for example.
    if (
      locationsContext?.state?.geocode &&
      (locationsContext?.state?.geocode?.latitude != geocode?.latitude ||
        locationsContext?.state?.geocode?.longitude != geocode?.longitude)
    ) {
      getModelDetails(
        homeDetails.isOnLand
          ? homeDetails.serialNumber
          : homeDetails.modelNumber,
        homeDetails.isOnLand,
        locationsContext.state.geocode.latitude,
        locationsContext.state.geocode.longitude,
        locationsContext.state.distance
      ).then((result) => {
        setHomeDetailsObject(result);
        let details = result;
        if (details == null) {
          return {
            redirect: {
              destination: '/find-a-home',
              permanent: false,
            },
          };
        } else {
          //      let dealerInfo = null; // Dealer and broker info if available (e.g. home with land).
          const { brokerageEmail, brokeragePhone, brokerageName } = details;

          // If there is a dealer Id number, get some dealer information.
          if (details?.dealerId && details?.dealerId !== 0) {
            getDealerLandingPageInfoById(details.dealerId).then((result) => {
              setDealerInfoObject({
                dealer: result?.dealer,
                brokerageEmail,
                brokeragePhone,
                brokerageName,
              });
            });
          }
        }
      });
    }
  }, []);

  useEffect(() => {
    setHomeDetailsObject(homeDetails);
  }, [homeDetails]);

  return testModels.includes(homeDetailsObject?.modelNumber) ||
    homeDetailsObject?.isOnLand ? (
    <HomeDetailsRedesign
      homeDetails={homeDetailsObject}
      pageInfo={pageInfo}
      imageGridData={imageGridData}
      dealerInfo={dealerInfoObject}
    />
  ) : (
    homeDetailsObject && <PreviousHomeDetails homeDetails={homeDetailsObject} />
  );
};

HomeDetails.propTypes = {
  homeDetails: PropTypes.shape({
    images: PropTypes.any,
    modelDescription: PropTypes.any,
    modelId: PropTypes.any,
    modelNumber: PropTypes.any,
    serialNumber: PropTypes.any,
    tags: PropTypes.any,
    modelFeatures: PropTypes.any,
    virtualTours: PropTypes.any,
    maxSquareFeet: PropTypes.any,
    baths: PropTypes.any,
    beds: PropTypes.any,
    isOnLand: PropTypes.any, // homes with land will have this prop
  }),
  pageInfo: PropTypes.object,
  imageGridData: PropTypes.array,
  dealerInfo: PropTypes.object,
  geocode: PropTypes.object,
};

const webPageKey = '1kK3LVNzyClRaQfE2nP8zA';

export async function getServerSideProps({ query }) {
  let homeDetails = null;
  let geocode = null;
  await locationService.getLocationByIpOrPostalCode().then((result) => {
    geocode = result;
  });

  await getModelDetails(
    query.modelNumber,
    query.isOnLand,
    geocode?.latitude,
    geocode?.longitude,
    50
  )
    .then((result) => {
      homeDetails = result;
    })
    .catch((e) => {
      console.error(
        `Error retrieving model details for modelNumber "${query.modelNumber}" - query ${JSON.stringify(query)} - lat/lng ${geocode?.latitude}, ${geocode?.longitude} - (${e})`
      );
      return {
        redirect: {
          destination: '/find-a-home',
          permanent: false,
        },
      };
    });

  if (homeDetails == null) {
    return {
      redirect: {
        destination: '/find-a-home',
        permanent: false,
      },
    };
  } else {
    let webPageContent = null;
    let imageGridData = null;
    let dealerInfo = null; // Dealer and broker info if available (e.g. home with land).
    const { brokerageEmail, brokeragePhone, brokerageName } = homeDetails;

    // If there is a dealer Id number, get some dealer information.
    if (homeDetails?.dealerId && homeDetails?.dealerId !== 0) {
      await getDealerLandingPageInfoById(homeDetails.dealerId).then(
        (result) => {
          dealerInfo = {
            dealer: result?.dealer,
            brokerageEmail,
            brokeragePhone,
            brokerageName,
          };
        }
      );
    }
    await Promise.all([
      getWebpageTemplateContent(webPageKey)
        .then((response) => {
          webPageContent = response;
        })
        .catch(() => {
          console.error('no content');
        }),

      getDynamicImageGrid(query.modelNumber)
        .then((response) => {
          imageGridData = response;
        })
        .catch(() => {
          console.error('no content');
        }),
    ]);

    return {
      props: {
        homeDetails,
        pageInfo: webPageContent,
        imageGridData,
        dealerInfo,
        geocode,
      },
    };
  }
}

export default HomeDetails;
